import { useState } from 'react';
import type { KioskSessionView } from '@print-saas/shared';
import { ClockIcon, PhoneIcon } from '../components/icons';
import { MessageScreen } from '../components/MessageScreen';
import { useNow } from '../hooks/useNow';
import { formatCountdown, formatPrice } from '../lib/format';

/** A phone's page normally connects within a second of scanning: only then is "still connecting" worth a countdown. */
const CONNECTING_QUIET_MS = 5_000;

export function ActiveScreen({ session }: { session: KioskSessionView }) {
  if (session.customerLeftDeadline) return <CustomerLeftScreen session={session} deadline={session.customerLeftDeadline} />;
  if (session.customerConnectingDeadline) return <ConnectingScreen deadline={session.customerConnectingDeadline} />;
  return (
    <MessageScreen
      tone="info"
      icon={<PhoneIcon />}
      title="Customer connected"
      subtitle="Upload your files and choose print options on your phone."
    >
      <div className="mt-[2vmin] grid w-full max-w-[110vmin] grid-cols-3 gap-[2.4vmin]">
        <Stat label="Files" value={String(session.fileCount)} />
        <Stat label="Sheets" value={String(session.totalSheets)} />
        <Stat label="Total" value={formatPrice(session.totalAmount)} highlight />
      </div>
    </MessageScreen>
  );
}

/** The phone disconnected; the session ends at `deadline` unless the customer comes back. */
function CustomerLeftScreen({ session, deadline }: { session: KioskSessionView; deadline: string }) {
  const now = useNow();
  return (
    <MessageScreen
      tone="warn"
      icon={<ClockIcon />}
      title="Customer left"
      subtitle={
        session.fileCount > 0
          ? 'Reopen the page on your phone to continue your order.'
          : 'Reopen the page on your phone to continue.'
      }
    >
      <Countdown label="Kiosk frees in" ms={Date.parse(deadline) - now} large />
    </MessageScreen>
  );
}

/** Scanned, but the phone's page hasn't connected (e.g. it lost signal): the session ends at `deadline` unless it does. */
function ConnectingScreen({ deadline }: { deadline: string }) {
  const now = useNow();
  const [since] = useState(Date.now);
  const waited = now - since >= CONNECTING_QUIET_MS;
  return (
    <MessageScreen
      tone={waited ? 'warn' : 'info'}
      icon={<PhoneIcon />}
      title={waited ? 'Waiting for the phone' : 'Customer connected'}
      subtitle={
        waited
          ? "The page hasn't opened on the phone yet. Check its internet connection and reload the page."
          : 'Upload your files and choose print options on your phone.'
      }
    >
      {waited && <Countdown label="Kiosk frees in" ms={Date.parse(deadline) - now} large />}
    </MessageScreen>
  );
}

export function Countdown({ label, ms, large }: { label: string; ms: number; large?: boolean }) {
  return (
    <div className="flex flex-col items-center" role="timer">
      <div className="text-[2.4vmin] font-semibold tracking-widest text-muted uppercase">{label}</div>
      <div className={`${large ? 'text-[11vmin]' : 'text-[6.5vmin]'} leading-none font-black tabular-nums`}>
        {formatCountdown(ms)}
      </div>
    </div>
  );
}

export function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      className={`rounded-[2.4vmin] px-[2vmin] py-[2.6vmin] ring-1 ${
        highlight ? 'bg-accent/10 ring-accent/40' : 'bg-panel/80 ring-line'
      }`}
    >
      <div className="text-[2.2vmin] font-semibold tracking-wider text-muted uppercase">{label}</div>
      {/* Keyed by value so each change replays the "pop" animation. */}
      <div
        key={value}
        className={`animate-pop mt-1 text-[6.5vmin] leading-none font-black tabular-nums ${
          highlight ? 'text-accent' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}
